// frontend/src/hooks/useTurnstile.ts
// Cloudflare Turnstile integration for bot protection
//
// Usage:
//   const { getToken, TurnstileWidget, isReady, error } = useTurnstile();
//   
//   // Include widget in component (invisible by default)
//   <TurnstileWidget />
//   
//   // Get token when needed
//   const token = await getToken();

import { useState, useCallback, useRef, useEffect } from 'react';

// Turnstile site keys are public. Keep the production key as a fallback so
// guest-protected flows still work if the frontend build environment omits it.
const DEFAULT_TURNSTILE_SITE_KEY = '0x4AAAAAACPKno1NIGFKbd8W';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || DEFAULT_TURNSTILE_SITE_KEY;

// Add Turnstile types
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
      remove: (widgetId: string) => void;
      ready: (callback: () => void) => void;
      execute: (container: string | HTMLElement, options?: Partial<TurnstileOptions>) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: (error: unknown) => void;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'flexible' | 'compact' | 'invisible';
  action?: string;
  cData?: string;
  'response-field'?: boolean;
  'response-field-name'?: string;
  retry?: 'auto' | 'never';
  'retry-interval'?: number;
  'refresh-expired'?: 'auto' | 'manual' | 'never';
  appearance?: 'always' | 'execute' | 'interaction-only';
  execution?: 'render' | 'execute';
}

interface UseTurnstileReturn {
  getToken: () => Promise<string | null>;
  TurnstileWidget: React.FC<{ className?: string }>;
  isReady: boolean;
  isEnabled: boolean;
  error: string | null;
  reset: () => void;
}

export function useTurnstile(): UseTurnstileReturn {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resolveRef = useRef<((token: string | null) => void) | null>(null);
  const scriptLoadedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);

  // Check if Turnstile is enabled
  const isEnabled = !!TURNSTILE_SITE_KEY;

  // Load Turnstile script
  useEffect(() => {
    if (!isEnabled || scriptLoadedRef.current) return;

    // Check if script is already loaded
    if (window.turnstile) {
      setIsReady(true);
      scriptLoadedRef.current = true;
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      // Wait for it to load
      window.onTurnstileLoad = () => {
        setIsReady(true);
        scriptLoadedRef.current = true;
      };
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    // Cloudflare's current docs require ?render=explicit when using turnstile.render()
    // in SPA/dynamic contexts like ours.
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad';
    script.async = true;
    script.defer = true;

    window.onTurnstileLoad = () => {
      setIsReady(true);
      scriptLoadedRef.current = true;
    };

    script.onerror = () => {
      setError('Failed to load Turnstile script');
    };

    document.head.appendChild(script);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      resolveRef.current = null;
      // Cleanup widget on unmount
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [isEnabled]);

  // Initialize widget when container is available and script is ready
  const initWidget = useCallback((container: HTMLDivElement) => {
    if (!window.turnstile || !TURNSTILE_SITE_KEY || widgetIdRef.current) return;

    try {
      const useCompactWidget = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 420px)').matches;

      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        size: useCompactWidget ? 'compact' : 'flexible',
        appearance: 'interaction-only',  // Only show when user interaction required
        // Defer execution until the user actually tries to submit. This matches
        // Cloudflare's documented explicit-render SPA flow.
        execution: 'execute',
        callback: (newToken: string) => {
          if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          setToken(newToken);
          setError(null);
          if (resolveRef.current) {
            resolveRef.current(newToken);
            resolveRef.current = null;
          }
        },
        'error-callback': (err: unknown) => {
          if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          console.error('Turnstile error:', err);
          setError('Verification failed. Please try again.');
          setToken(null);
          if (resolveRef.current) {
            resolveRef.current(null);
            resolveRef.current = null;
          }
        },
        'expired-callback': () => {
          if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          setToken(null);
        },
        theme: 'auto',
        retry: 'auto',
        'refresh-expired': 'auto',
      });
    } catch (err) {
      console.error('Failed to render Turnstile:', err);
      setError('Failed to initialize verification');
    }
  }, []);

  // Get token (triggers challenge if needed)
  const getToken = useCallback(async (): Promise<string | null> => {
    // If Turnstile is not enabled, return null (will skip verification)
    if (!isEnabled) {
      return null;
    }

    // If we have a valid token, return it and clear it
    // (Turnstile tokens are single-use - once verified by backend, they're consumed)
    if (token) {
      const tokenToReturn = token;
      setToken(null);  // Clear React state
      // MUST also reset widget to clear its internal getResponse() value
      // Otherwise the next call to getToken() would return the same consumed token
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
      return tokenToReturn;
    }

    if (!window.turnstile || !widgetIdRef.current || !containerRef.current) {
      await new Promise<void>((resolve) => {
        const startedAt = Date.now();
        const checkReady = window.setInterval(() => {
          if (window.turnstile && widgetIdRef.current && containerRef.current) {
            window.clearInterval(checkReady);
            resolve();
            return;
          }

          if (Date.now() - startedAt >= 15000) {
            window.clearInterval(checkReady);
            resolve();
          }
        }, 100);
      });

      if (!window.turnstile || !widgetIdRef.current || !containerRef.current) {
        setError('Verification not available');
        return null;
      }
    }

    // Check if we already have a response from the widget
    const existingResponse = window.turnstile.getResponse(widgetIdRef.current);
    if (existingResponse) {
      // Reset widget immediately so next call gets a fresh token
      // (Turnstile tokens are single-use)
      window.turnstile.reset(widgetIdRef.current);
      return existingResponse;
    }

    // Need to get a fresh token - reset triggers a new challenge in render mode
    return new Promise((resolve) => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      resolveRef.current = resolve;
      setError(null);
      
      // Clear any prior state, then execute the challenge on demand.
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        if (containerRef.current) {
          window.turnstile.execute(containerRef.current);
        }
      }

      // Timeout after 30 seconds
      timeoutRef.current = window.setTimeout(() => {
        if (resolveRef.current) {
          resolveRef.current(null);
          resolveRef.current = null;
          timeoutRef.current = null;
          setError('Verification timed out');
        }
      }, 30000);
    });
  }, [isEnabled, isReady, token]);

  // Reset the widget
  const reset = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    resolveRef.current = null;
    setToken(null);
    setError(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  // Turnstile widget component
  // With 'interaction-only' appearance, widget only shows when user verification needed
  // Position fixed at bottom-right so it's visible but not intrusive
  const TurnstileWidget: React.FC<{ className?: string }> = useCallback(
    ({ className }) => {
      if (!isEnabled) return null;

      return (
        <div
          ref={(el) => {
            if (el && el !== containerRef.current) {
              containerRef.current = el;
              if (isReady) {
                initWidget(el);
              }
            }
          }}
          className={className}
          style={{
            position: 'fixed',
            bottom: 'max(16px, env(safe-area-inset-bottom))',
            right: 'max(16px, env(safe-area-inset-right))',
            width: 'min(300px, calc(100vw - 32px))',
            maxWidth: 'calc(100vw - 32px)',
            zIndex: 9999,
          }}
        />
      );
    },
    [isEnabled, isReady, initWidget]
  );

  // Initialize widget when ready state changes
  useEffect(() => {
    if (isReady && containerRef.current && !widgetIdRef.current) {
      initWidget(containerRef.current);
    }
  }, [isReady, initWidget]);

  return {
    getToken,
    TurnstileWidget,
    isReady,
    isEnabled,
    error,
    reset,
  };
}
